import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { withRouter, RouteComponentProps, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import classnames from "classnames";

import i18n from "../../i18n";
import { SelectListDto } from "../../models";
import { SubjectCard, TableSpinner } from "../../components";
import { BreadCrumb, NoResultFound } from "../../components/Common";
import { LocalizedProperty, PaginateQuery } from "../../interfaces";
import { EducationalStageDto } from "../../interfaces/curriculum";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { useGetGradeSelectListByCourseQuery } from "../../store/api/gradeApi";
import {
  useGetSubjectsByGradeQuery,
  useDeleteSubjectMutation,
  useToggleSubjectStateMutation,
} from "../../store/api/subjectApi";
import { useGetEducationSystemSelectListQuery } from "../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../store/api/curriculumApi";
import { useGetEducationalStagesQuery } from "../../store/api/educationalStageApi";
import { RoleEnum } from "../../enums";
import { AddEditSubjectModal } from "./AddEditSubjectModal";
import DeleteModal from "../../components/Common/DeleteModal";
import { CopyCutSubjectModal } from "./CopyCutSubjectModal";
import { SyncSubjectModal } from "./SyncSubjectModal";
import { AccountState } from "../../store/slices/accountSlice";
import {
  buildSubjectsSearch,
  getCourseSlug,
  readSubjectsFiltersFromSearch,
} from "../../utils/subjectsNavigation";
// @ts-ignore
import "./SubjectsTabPage.scss";

type TProps = RouteComponentProps & {};

const hasFullSubjectAccess = (roleKey?: string) =>
  roleKey === RoleEnum.SUPER_ADMIN ||
  roleKey === RoleEnum.ADMIN ||
  roleKey === RoleEnum.SYSTEM_ADMIN ||
  roleKey === RoleEnum.HEAD_TEACHER_ADMIN;

const SubjectsTabPageComponent: React.FC<TProps> = ({ history, location }) => {
  const { t } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const urlFilters = useMemo(
    () => readSubjectsFiltersFromSearch(location.search),
    [location.search],
  );

  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const isSuperAdmin = profile?.role?.key === RoleEnum.SUPER_ADMIN;
  const fullAccess = hasFullSubjectAccess(profile?.role?.key);
  const isTeacherAdmin = profile?.role?.key === RoleEnum.TEACHER_ADMIN;

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const selectedEducationSystemId = useMemo(() => {
    if (!educationSystems?.length) return urlFilters.educationSystemId;
    if (
      urlFilters.educationSystemId &&
      educationSystems.some((s: any) => s.id === urlFilters.educationSystemId)
    ) {
      return urlFilters.educationSystemId;
    }
    return educationSystems[0].id;
  }, [educationSystems, urlFilters.educationSystemId]);

  const { data: curricula } = useGetCurriculumSelectListQuery(
    selectedEducationSystemId || undefined,
    { skip: !selectedEducationSystemId },
  );
  const selectedCurriculumId = useMemo(() => {
    if (!curricula?.length) return "";
    if (
      urlFilters.curriculumId &&
      curricula.some((c: any) => c.id === urlFilters.curriculumId)
    ) {
      return urlFilters.curriculumId;
    }
    return curricula[0].id;
  }, [curricula, urlFilters.curriculumId]);

  const { data: allCourses } = useGetCourseSelectListQuery();
  const visibleCourses = useMemo(() => {
    if (!allCourses?.length) return [];
    if (fullAccess) return allCourses;
    const allowedIds = new Set((profile?.courses || []).map((c) => c.id));
    return allCourses.filter((c: any) => allowedIds.has(c.id));
  }, [allCourses, fullAccess, profile?.courses]);

  const selectedCourseId = useMemo(() => {
    if (!visibleCourses.length) return "";
    if (
      urlFilters.courseId &&
      visibleCourses.some((c: any) => c.id === urlFilters.courseId)
    ) {
      return urlFilters.courseId;
    }
    return visibleCourses[0].id;
  }, [visibleCourses, urlFilters.courseId]);

  const paginateQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId: selectedCurriculumId,
      courseId: selectedCourseId,
    };
    return q;
  }, [selectedCurriculumId, selectedCourseId]);

  const {
    currentData: stagesResponse,
    isLoading: loadingStages,
    isFetching: fetchingStages,
  } = useGetEducationalStagesQuery(paginateQuery, {
    skip: !selectedCurriculumId || !selectedCourseId,
  });

  const { data: allowedGrades = [] } = useGetGradeSelectListByCourseQuery(
    selectedCourseId,
    { skip: !selectedCourseId || !isTeacherAdmin },
  );
  const allowedGradeIds = useMemo(
    () => new Set((allowedGrades || []).map((g: SelectListDto) => g.id)),
    [allowedGrades],
  );

  const stages: EducationalStageDto[] = useMemo(() => {
    const raw = stagesResponse?.data || [];
    if (!isTeacherAdmin) return raw;
    return raw
      .map((stage) => ({
        ...stage,
        grades: stage.grades.filter((g) => allowedGradeIds.has(g.id)),
      }))
      .filter((stage) => stage.grades.length > 0);
  }, [stagesResponse?.data, isTeacherAdmin, allowedGradeIds]);

  const selectedStageId = useMemo(() => {
    if (!stages.length) return "";

    // Explicit stage selection (including empty stages) must stick.
    if (urlFilters.stageId) {
      const urlStage = stages.find((s) => s.id === urlFilters.stageId);
      if (urlStage) return urlStage.id;
    }

    // Legacy / grade-only URLs — find the stage that owns the grade.
    if (urlFilters.gradeId) {
      const stageWithGrade = stages.find((s) =>
        s.grades.some((g) => g.id === urlFilters.gradeId),
      );
      if (stageWithGrade) return stageWithGrade.id;
    }

    // Default: first stage that has grades, else first stage.
    return stages.find((s) => s.grades.length > 0)?.id || stages[0].id;
  }, [stages, urlFilters.stageId, urlFilters.gradeId]);

  const selectedStage = useMemo(
    () => stages.find((s) => s.id === selectedStageId),
    [stages, selectedStageId],
  );

  const stageGrades = selectedStage?.grades || [];

  const selectedGradeId = useMemo(() => {
    if (!stageGrades.length) return "";
    if (
      urlFilters.gradeId &&
      stageGrades.some((g) => g.id === urlFilters.gradeId)
    ) {
      return urlFilters.gradeId;
    }
    return stageGrades[0].id;
  }, [stageGrades, urlFilters.gradeId]);

  const courseSlug = getCourseSlug(allCourses, selectedCourseId);

  const {
    currentData: subjectsByGradeCurrent,
    isLoading: subjectsByGradeLoading,
    isFetching: subjectsByGradeFetching,
  } = useGetSubjectsByGradeQuery(selectedGradeId, { skip: !selectedGradeId });
  // RTK Query v2 keeps previous `data` while args change — only show results for the active grade.
  const subjectsByGrade = subjectsByGradeCurrent ?? [];
  const subjectsPending =
    Boolean(selectedGradeId) &&
    subjectsByGradeCurrent === undefined &&
    (subjectsByGradeLoading || subjectsByGradeFetching);

  const [
    deleteSubject,
    { isLoading: deleteSubjectLoading, error: deleteSubjectError },
  ] = useDeleteSubjectMutation();
  const [toggleSubjectState] = useToggleSubjectStateMutation();

  const [showAddEditSubjectModal, setShowAddEditSubjectModal] = useState(false);
  const [editingSubjectId, setEditingSubjectId] = useState<string | null>(null);
  const [editingSequenceNumber, setEditingSequenceNumber] = useState<
    number | null
  >(null);
  const [showCopyCutSubjectModal, setShowCopyCutSubjectModal] = useState(false);
  const [showSyncSubjectModal, setShowSyncSubjectModal] = useState(false);
  const [copyingCutSubjectId, setCopyingCutSubjectId] = useState<string | null>(
    null,
  );
  const [syncingSubjectId, setSyncingSubjectId] = useState<string | null>(null);
  const [copyingCutSubjectName, setCopyingCutSubjectName] =
    useState<LocalizedProperty | null>(null);
  const [syncingSubjectName, setSyncingSubjectName] =
    useState<LocalizedProperty | null>(null);
  const [action, setAction] = useState<string>("copy");
  const [showDeleteSubjectModal, setShowDeleteSubjectModal] = useState(false);
  const [deletingSubjectId, setDeletingSubjectId] = useState<string | null>(
    null,
  );

  document.title = `${t("documentTitle", { pageName: "subjects" })}`;

  const syncFiltersToUrl = (
    next: {
      educationSystemId?: string;
      curriculumId?: string;
      courseId?: string;
      stageId?: string;
      gradeId?: string;
    },
    mode: "push" | "replace" = "push",
  ) => {
    const search = buildSubjectsSearch({
      educationSystemId: next.educationSystemId ?? selectedEducationSystemId,
      curriculumId: next.curriculumId ?? selectedCurriculumId,
      courseId: next.courseId ?? selectedCourseId,
      stageId: next.stageId ?? selectedStageId,
      gradeId: next.gradeId ?? selectedGradeId,
    });
    if (search === location.search) return;
    if (mode === "push") {
      history.push({ pathname: "/subjects", search });
    } else {
      history.replace({ pathname: "/subjects", search });
    }
  };

  useEffect(() => {
    if (
      !selectedEducationSystemId ||
      !selectedCurriculumId ||
      !selectedCourseId
    ) {
      return;
    }

    const resolvedStageId =
      selectedStageId ||
      stages.find((s) => s.grades.length > 0)?.id ||
      stages[0]?.id ||
      "";
    const resolvedGradeId =
      selectedGradeId ||
      stages.find((s) => s.id === resolvedStageId)?.grades[0]?.id ||
      "";

    if (
      urlFilters.educationSystemId === selectedEducationSystemId &&
      urlFilters.curriculumId === selectedCurriculumId &&
      urlFilters.courseId === selectedCourseId &&
      urlFilters.stageId === resolvedStageId &&
      urlFilters.gradeId === resolvedGradeId
    ) {
      return;
    }

    syncFiltersToUrl(
      {
        educationSystemId: selectedEducationSystemId,
        curriculumId: selectedCurriculumId,
        courseId: selectedCourseId,
        stageId: resolvedStageId,
        gradeId: resolvedGradeId,
      },
      "replace",
    );
  }, [
    selectedEducationSystemId,
    selectedCurriculumId,
    selectedCourseId,
    selectedStageId,
    selectedGradeId,
    stages,
    urlFilters.educationSystemId,
    urlFilters.curriculumId,
    urlFilters.courseId,
    urlFilters.stageId,
    urlFilters.gradeId,
  ]);

  const onAddSubject = () => {
    if (!selectedCourseId || !selectedGradeId) return;
    setEditingSubjectId(null);
    setEditingSequenceNumber(null);
    setShowAddEditSubjectModal(true);
  };

  const onEditSubject = (id: string) => {
    if (selectedCourseId && selectedGradeId) {
      const subject = subjectsByGrade?.find((s: any) => s.id === id);
      setEditingSubjectId(id);
      setEditingSequenceNumber(subject?.sequenceNumber ?? null);
      setShowAddEditSubjectModal(true);
    }
  };

  const onToggleState = async (id: string) => {
    if (selectedCourseId && selectedGradeId) {
      try {
        await toggleSubjectState(id).unwrap();
      } catch (err) {}
    }
  };

  const onDeleteSubject = (id: string) => {
    setDeletingSubjectId(id);
    setShowDeleteSubjectModal(true);
  };

  const onCopySubject = (id: string) => {
    setCopyingCutSubjectName(
      subjectsByGrade?.find((s: any) => s.id === id)?.name || null,
    );
    setCopyingCutSubjectId(id);
    setShowCopyCutSubjectModal(true);
    setAction("copy");
  };

  const onCutSubject = (id: string) => {
    setCopyingCutSubjectName(
      subjectsByGrade?.find((s: any) => s.id === id)?.name || null,
    );
    setCopyingCutSubjectId(id);
    setShowCopyCutSubjectModal(true);
    setAction("cut");
  };

  const onSyncSubject = (id: string) => {
    setSyncingSubjectName(
      subjectsByGrade?.find((s: any) => s.id === id)?.name || null,
    );
    setSyncingSubjectId(id);
    setShowSyncSubjectModal(true);
  };

  const onDeleteSubjectConfirm = async () => {
    if (deletingSubjectId) {
      try {
        await deleteSubject(deletingSubjectId).unwrap();
        setDeletingSubjectId(null);
        setShowDeleteSubjectModal(false);
      } catch (err) {}
    }
  };

  const isLoading =
    loadingStages || (fetchingStages && stagesResponse === undefined);

  const renderSubjectGrid = () => (
    <Row className="team-list grid-view-filter">
      {subjectsPending ? (
        <Col>
          <TableSpinner />
        </Col>
      ) : subjectsByGrade.length > 0 ? (
        subjectsByGrade.map((item: any) => (
          <Col key={item.id}>
            <SubjectCard
              link={`/subjects/${courseSlug}/${selectedGradeId}/${item.id}${buildSubjectsSearch(
                {
                  educationSystemId: selectedEducationSystemId,
                  curriculumId: selectedCurriculumId,
                  courseId: selectedCourseId,
                  stageId: selectedStageId,
                  gradeId: selectedGradeId,
                },
              )}`}
              {...item}
              sequenceNumber={item?.sequenceNumber || 0}
              onEditSubject={onEditSubject}
              onDeleteSubject={onDeleteSubject}
              onCopySubject={onCopySubject}
              onCutSubject={onCutSubject}
              onSyncSubject={onSyncSubject}
              onToggleState={onToggleState}
            />
          </Col>
        ))
      ) : (
        <Col>
          <NoResultFound />
        </Col>
      )}
    </Row>
  );

  const renderFilterChip = (
    key: string,
    label: string,
    active: boolean,
    onClick: () => void,
  ) => (
    <button
      key={key}
      type="button"
      className={classnames("subjects-filter-chip", { active })}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const getGradeLabel = (grade: {
    id: string;
    name?: LocalizedProperty;
    value?: LocalizedProperty;
  }) =>
    grade.name?.[langKey] ||
    grade.value?.[langKey] ||
    grade.name?.en ||
    grade.value?.en ||
    "";

  const educationalStagesLink = `/education-system/educational-stages${buildSubjectsSearch(
    {
      educationSystemId: selectedEducationSystemId,
      curriculumId: selectedCurriculumId,
      courseId: selectedCourseId,
    },
  )}`;

  const renderNoStagesEmptyState = () => (
    <Card>
      <CardBody className="text-center text-muted py-5">
        {isSuperAdmin ? (
          <>
            {t("noStagesYetLead")}{" "}
            <Link to={educationalStagesLink} className="fw-medium">
              {t("addStage")}
            </Link>{" "}
            {t("noStagesYetTail")}
          </>
        ) : (
          t("noStagesYet")
        )}
      </CardBody>
    </Card>
  );

  const renderHierarchyFilters = (
    grades: Array<{
      id: string;
      name?: LocalizedProperty;
      value?: LocalizedProperty;
    }>,
    onGradeSelect: (gradeId: string) => void,
    showStages: boolean,
  ) => (
    <Card className="subjects-hierarchy-filters mb-3">
      <CardBody className="py-3 px-3">
        {showStages && stages.length > 0 ? (
          <div className="subjects-filter-row">
            <span className="subjects-filter-label">
              {t("educationalStages")}
            </span>
            <div className="subjects-filter-chips">
              {stages.map((stage) =>
                renderFilterChip(
                  stage.id,
                  stage.name[langKey],
                  selectedStageId === stage.id,
                  () => {
                    syncFiltersToUrl({
                      educationSystemId: selectedEducationSystemId,
                      curriculumId: selectedCurriculumId,
                      courseId: selectedCourseId,
                      stageId: stage.id,
                      gradeId: stage.grades[0]?.id || "",
                    });
                  },
                ),
              )}
            </div>
          </div>
        ) : null}
        {grades.length > 0 ? (
          <div className="subjects-filter-row subjects-filter-row--grades">
            <span className="subjects-filter-label">{t("grade")}</span>
            <div className="subjects-filter-chips">
              {grades.map((grade) =>
                renderFilterChip(
                  grade.id,
                  getGradeLabel(grade),
                  selectedGradeId === grade.id,
                  () => onGradeSelect(grade.id),
                ),
              )}
            </div>
            {isSuperAdmin && selectedGradeId ? (
              <div className="subjects-filter-row-actions">
                <Button color="success" size="sm" onClick={onAddSubject}>
                  <i className="ri-add-fill me-1 align-bottom"></i>
                  {t("addItem", { item: "subject" })}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("subjects")} pageTitle={t("menu")} />

          <Row className="mb-3 align-items-end g-3">
            <Col md={3}>
              <label className="form-label">{t("educationSystem")}</label>
              <Input
                type="select"
                value={selectedEducationSystemId}
                onChange={(e) => {
                  syncFiltersToUrl({
                    educationSystemId: e.target.value,
                    curriculumId: "",
                    courseId: selectedCourseId,
                    stageId: "",
                    gradeId: "",
                  });
                }}
              >
                {(educationSystems || []).map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.value?.[langKey] || s.value?.en}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <label className="form-label">{t("curriculumItem")}</label>
              <Input
                type="select"
                value={selectedCurriculumId}
                onChange={(e) => {
                  syncFiltersToUrl({
                    educationSystemId: selectedEducationSystemId,
                    curriculumId: e.target.value,
                    courseId: selectedCourseId,
                    stageId: "",
                    gradeId: "",
                  });
                }}
                disabled={!selectedEducationSystemId}
              >
                {(curricula || []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.value?.[langKey] || c.value?.en}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          {visibleCourses.length > 0 ? (
            <Nav tabs className="nav-tabs-custom mb-3">
              {visibleCourses.map((course: any) => (
                <NavItem key={course.id}>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({
                      active: selectedCourseId === course.id,
                    })}
                    onClick={() => {
                      syncFiltersToUrl({
                        educationSystemId: selectedEducationSystemId,
                        curriculumId: selectedCurriculumId,
                        courseId: course.id,
                        stageId: "",
                        gradeId: "",
                      });
                    }}
                  >
                    {course.value?.[langKey] || course.value?.en}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          ) : null}

          {isLoading ? (
            <TableSpinner />
          ) : !visibleCourses.length ? (
            <Card>
              <CardBody className="text-center text-muted py-5">
                {t("noResultFound")}
              </CardBody>
            </Card>
          ) : stages.length === 0 ? (
            renderNoStagesEmptyState()
          ) : (
            <>
              {renderHierarchyFilters(
                stageGrades,
                (gradeId) => {
                  syncFiltersToUrl({
                    educationSystemId: selectedEducationSystemId,
                    curriculumId: selectedCurriculumId,
                    courseId: selectedCourseId,
                    stageId: selectedStageId,
                    gradeId,
                  });
                },
                true,
              )}
              {stageGrades.length > 0 ? (
                <Row>
                  <Col xxl={12}>{renderSubjectGrid()}</Col>
                </Row>
              ) : (
                <Card>
                  <CardBody className="text-center text-muted py-5">
                    {t("noGradesAssigned")}
                  </CardBody>
                </Card>
              )}
            </>
          )}
        </Container>
      </div>

      <AddEditSubjectModal
        gradeId={selectedGradeId || ""}
        courseId={selectedCourseId || ""}
        id={editingSubjectId}
        initialSequenceNumber={editingSequenceNumber}
        show={showAddEditSubjectModal}
        onCloseClick={() => {
          setShowAddEditSubjectModal(false);
          setEditingSubjectId(null);
          setEditingSequenceNumber(null);
        }}
      />
      <CopyCutSubjectModal
        id={copyingCutSubjectId as string}
        name={copyingCutSubjectName}
        show={showCopyCutSubjectModal}
        action={action}
        defaultEducationSystemId={selectedEducationSystemId}
        defaultCurriculumId={selectedCurriculumId}
        defaultCourseId={selectedCourseId}
        defaultStageId={selectedStageId}
        defaultGradeId={selectedGradeId}
        onCloseClick={() => {
          setShowCopyCutSubjectModal(false);
          setCopyingCutSubjectId(null);
        }}
      />
      <SyncSubjectModal
        id={syncingSubjectId as string}
        name={syncingSubjectName}
        show={showSyncSubjectModal}
        defaultEducationSystemId={selectedEducationSystemId}
        defaultCurriculumId={selectedCurriculumId}
        defaultCourseId={selectedCourseId}
        defaultStageId={selectedStageId}
        defaultGradeId={selectedGradeId}
        onCloseClick={() => {
          setShowSyncSubjectModal(false);
          setSyncingSubjectId(null);
        }}
      />
      <DeleteModal
        loading={deleteSubjectLoading}
        show={showDeleteSubjectModal}
        onDeleteClick={onDeleteSubjectConfirm}
        onCloseClick={() => setShowDeleteSubjectModal(false)}
        error={
          deleteSubjectError
            ? (deleteSubjectError as any)?.data?.message || "An error occurred"
            : undefined
        }
      />
    </>
  );
};

export const SubjectsTabPage = withRouter(SubjectsTabPageComponent);
